import { ShieldCheck, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/content";
import { ComparisonCards } from "../components/membership/ComparisonCards";
import { BenefitsSection } from "../components/membership/BenefitsSection";
import { EnrollForm } from "../components/membership/EnrollForm";
import { BillingTermsSummary } from "../components/membership/BillingTermsSummary";
import { MembershipFAQ } from "../components/membership/MembershipFAQ";

const STEPS = [
  { n: "1", title: "Join in 2 Minutes", text: "Enroll online with secure Stripe checkout. Your membership activates instantly." },
  { n: "2", title: "Benefits Start Immediately", text: "Member pricing, 10% off eligible labor, and priority scheduling from day one." },
  { n: "3", title: "We Track Your Vehicle", text: "Add your vehicles to your portal garage — we keep your digital service history." },
  { n: "4", title: "Automatic & Flexible", text: "Renews monthly at $39.99 until canceled. Cancel future renewals any time from your portal." },
];

export default function Membership() {
  return (
    <main data-testid="membership-page" className="pt-20 md:pt-24">
      <section className="py-20 sm:py-28 border-b border-zinc-800/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500">Mason's Mobile Membership</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mt-4 mb-6 max-w-3xl">
            Vehicle Care That Pays for Itself
          </h1>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl mb-8">
            One membership. One complimentary maintenance service every year, 10% off eligible
            labor, priority scheduling, and a mechanic who already knows your vehicle.
          </p>
          <div className="flex flex-wrap items-center gap-6">
            <div data-testid="membership-price-hero">
              <span className="font-heading text-5xl font-bold text-white">$39.99</span>
              <span className="text-zinc-400 text-lg">/month</span>
            </div>
            <a
              href="#enroll"
              data-testid="membership-hero-join-button"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-8 py-4 font-semibold transition-colors"
            >
              Join Now
            </a>
          </div>
          <p className="text-zinc-500 text-sm mt-4 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-blue-500" />
            Renews automatically each month until canceled. Cancel any time.
          </p>
        </div>
      </section>

      <ComparisonCards />
      <BenefitsSection />

      <section className="py-16" data-testid="membership-how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-10">
            How the Membership Works
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-[#121212] border border-zinc-800 rounded-xl p-6">
                <div className="font-heading text-3xl font-bold text-blue-500 mb-3">{s.n}</div>
                <h3 className="font-heading font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-zinc-800/60 bg-[#0d0d0d]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <EnrollForm />
        </div>
      </section>

      <BillingTermsSummary />
      <MembershipFAQ />

      <section className="py-16 border-t border-zinc-800/60" data-testid="membership-contact">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-3">Questions Before Joining?</h2>
          <p className="text-zinc-400 mb-6">Call or text us — we'll walk you through it.</p>
          <a
            data-testid="membership-contact-call"
            href={PHONE_TEL}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-8 py-4 font-semibold transition-colors"
          >
            <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
          </a>
        </div>
      </section>
    </main>
  );
}
